import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCircle } from '@fortawesome/pro-light-svg-icons/faUserCircle';
import { UserAvatarProps } from './props';
import { TypedMemo } from '../../types';
import { avatarInitial, isPlaceholderAvatarUrl } from './avatarInitial';
import styles from './UserAvatar.module.scss';

/**
 * The round picture of an artist or a user. With no picture, or when the picture fails to load,
 * it shows the initial of `name` (else a person icon). It never shows a broken image.
 */
const UserAvatarComponent: React.FC<UserAvatarProps & { className?: string }> = ({
  size,
  url,
  name,
  danger = false,
  className,
  color,
  avatarStyle,
}) => {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  // A new address gets a new try.
  useEffect(() => setFailedUrl(null), [url]);
  const avatarSizeStyle = {
    width: size,
    height: size,
  };
  const showImage = !isPlaceholderAvatarUrl(url) && failedUrl !== url;
  const initial = avatarInitial(name);

  const renderFallback = () => {
    if (initial !== null) {
      return (
        <span
          className={styles.initial}
          style={{ ...avatarSizeStyle, fontSize: Math.max(9, Math.round(size * 0.42)), ...avatarStyle }}
          data-testid="user-avatar-initial"
          aria-hidden="true"
        >
          {initial}
        </span>
      );
    }
    return (
      <FontAwesomeIcon
        className="user-avatar-icon"
        icon={faUserCircle}
        style={{ ...avatarSizeStyle, ...avatarStyle }}
        color={color}
      />
    );
  };

  return (
    <div
      className={clsx('user-avatar-border rounded-circle', className, {
        'user-avatar-border-danger': danger,
        'user-avatar-border-white': !danger,
      })}
    >
      {showImage && typeof url === 'string' ? (
        <img
          className="ui-text rounded-circle"
          style={avatarSizeStyle}
          src={url}
          alt=""
          onError={() => setFailedUrl(url)}
        />
      ) : (
        renderFallback()
      )}
    </div>
  );
};

export const UserAvatar = TypedMemo(UserAvatarComponent);
